import { toStatus } from '@/features/cases/api/caseWire'
import { toUnitKind } from '@/lib/legUnits'
import type {
  CounterpartiesMoney,
  CounterpartyMoneyItem,
  CounterpartyStatement,
  CounterpartyStatementItem,
  CounterpartyStatementRow,
  Money,
  MoneyUnavailable,
  Settlement,
} from '@/types/domain'

/* The seam between the server's spelling of the counterparty money reads and
 * the client's (cmd/asa/readmodel_counterparties.go, S-22e). What happens here
 * and only here:
 *
 *   - a JSON `null` becomes an absent field — `averageRate` with no unit to
 *     average over, `settlement` on a unit the counterparty has no actual on —
 *     because every screen tests presence rather than null;
 *   - a case status is folded through the case list's `toStatus` and a unit
 *     kind through `toUnitKind`, so each vocabulary keeps one owner;
 *   - a word this build does not know is dropped rather than printed: an item
 *     kind a later deploy adds is left out of the expansion, an unknown
 *     settlement shows no state. Arrays stay arrays — the server sends `[]`,
 *     never `null`, and so does this mapping.
 *
 * Nothing is computed: every figure is the server's decimal string. */

export interface CounterpartyStatementItemWire {
  kind: string
  unitId?: string
  legId?: string
  legPosition?: number
  unitKind?: string
  number?: string
  carried?: boolean
  billed?: Money
  settlement?: string | null
  amount?: Money
}

export interface CounterpartyStatementRowWire {
  caseId: string
  number: string
  status: string
  openedOn: string
  route: { loading: string; border: string; discharge: string; unloading: string }
  units: number
  money?: { billed: Money; paid: Money; balance: Money; running: Money; advance: Money }
  items: CounterpartyStatementItemWire[]
}

export interface CounterpartyStatementWire {
  counterpartyId: string
  year: number | null
  years: number[]
  scoped: boolean
  moneyUnavailable?: MoneyUnavailable
  opening?: { balance: Money }
  totals?: {
    units: number
    billed: Money
    paid: Money
    balance: Money
    advance: Money
    averageRate: Money | null
  }
  rows: CounterpartyStatementRowWire[]
}

export interface CounterpartyMoneyItemWire {
  counterpartyId: string
  units: number
  billed: Money
  paid: Money
  balance: Money
  advance: Money
}

export interface CounterpartiesMoneyWire {
  scoped: boolean
  moneyUnavailable?: MoneyUnavailable
  items: CounterpartyMoneyItemWire[]
}

const SETTLEMENTS: readonly Settlement[] = ['unpaid', 'partly-paid', 'paid']

function toSettlement(wire: string | null | undefined): Settlement | undefined {
  return SETTLEMENTS.find((settlement) => settlement === wire)
}

function toItem(wire: CounterpartyStatementItemWire): CounterpartyStatementItem | undefined {
  const settlement = toSettlement(wire.settlement)
  switch (wire.kind) {
    case 'unit':
      return {
        kind: 'unit',
        unitId: wire.unitId ?? '',
        legId: wire.legId ?? '',
        legPosition: wire.legPosition ?? 0,
        unitKind: toUnitKind(wire.unitKind),
        number: wire.number ?? '',
        carried: wire.carried === true,
        ...(wire.billed ? { billed: wire.billed } : {}),
        ...(settlement ? { settlement } : {}),
      }
    case 'leg':
      return wire.billed
        ? {
            kind: 'leg',
            legId: wire.legId ?? '',
            legPosition: wire.legPosition ?? 0,
            billed: wire.billed,
            ...(settlement ? { settlement } : {}),
          }
        : undefined
    case 'other':
      return wire.billed
        ? { kind: 'other', billed: wire.billed, ...(settlement ? { settlement } : {}) }
        : undefined
    case 'advance':
      return wire.amount ? { kind: 'advance', amount: wire.amount } : undefined
    default:
      return undefined
  }
}

function toRow(wire: CounterpartyStatementRowWire): CounterpartyStatementRow {
  return {
    caseId: wire.caseId,
    number: wire.number,
    status: toStatus(wire.status),
    openedOn: wire.openedOn,
    route: wire.route,
    units: wire.units,
    ...(wire.money ? { money: wire.money } : {}),
    items: (wire.items ?? [])
      .map(toItem)
      .filter((item): item is CounterpartyStatementItem => item !== undefined),
  }
}

export function toCounterpartyStatement(wire: CounterpartyStatementWire): CounterpartyStatement {
  const totals = wire.totals && {
    units: wire.totals.units,
    billed: wire.totals.billed,
    paid: wire.totals.paid,
    balance: wire.totals.balance,
    advance: wire.totals.advance,
    ...(wire.totals.averageRate ? { averageRate: wire.totals.averageRate } : {}),
  }
  return {
    counterpartyId: wire.counterpartyId,
    year: wire.year,
    years: wire.years ?? [],
    scoped: wire.scoped === true,
    ...(wire.moneyUnavailable ? { moneyUnavailable: wire.moneyUnavailable } : {}),
    ...(wire.opening ? { opening: wire.opening } : {}),
    ...(totals ? { totals } : {}),
    rows: (wire.rows ?? []).map(toRow),
  }
}

export function toCounterpartiesMoney(wire: CounterpartiesMoneyWire): CounterpartiesMoney {
  return {
    scoped: wire.scoped === true,
    ...(wire.moneyUnavailable ? { moneyUnavailable: wire.moneyUnavailable } : {}),
    items: (wire.items ?? []).map(
      (item): CounterpartyMoneyItem => ({
        counterpartyId: item.counterpartyId,
        units: item.units,
        billed: item.billed,
        paid: item.paid,
        balance: item.balance,
        advance: item.advance,
      }),
    ),
  }
}

/** True for a served amount that is zero in any spelling — "0", "0.00",
 *  "-0.00". A comparison of the string, never arithmetic: the advance line and
 *  the advance item are shown only when there is one. */
export function isZeroAmount(amount: string): boolean {
  return /^[-+]?0*(\.0*)?$/.test(amount.trim()) && /\d/.test(amount)
}
